import { CalendarDate, DateRange, NumberRangeValue, parseDate, Time } from "@tournated-com/ui";
import { createParser, parseAsBoolean } from "nuqs";

/**
 * URL state for the "Include connected platforms" filter checkbox
 * (`IncludeConnectedPlatformsField`). Default true — the URL only carries the
 * key when the user opts out, matching the API param's server-side default.
 */
export const parseAsIncludeConnected = parseAsBoolean.withDefault(true);

export const parseAsDateRange = createParser<DateRange<CalendarDate>>({
  parse: (value) => {
    if (!value) return null;
    const [start, end] = value.split("~");
    if (!start || !end) return null;
    return { start: parseDate(start), end: parseDate(end) };
  },
  serialize: (val) => {
    return `${val.start}~${val.end}`;
  },
});

export const parseAsNumberRange = createParser<NumberRangeValue>({
  parse: (value) => {
    if (!value) return null;
    const [startStr, endStr] = value.split("~");
    if (!startStr || !endStr) return null;

    const start =
      startStr === "-Infinity" ? null : isNaN(Number(startStr)) ? null : Number(startStr);

    const end = endStr === "+Infinity" ? null : isNaN(Number(endStr)) ? null : Number(endStr);

    return { start, end };
  },
  serialize: (val) => {
    if (!val) return "";
    return `${val.start ?? "-Infinity"}~${val.end ?? "+Infinity"}`;
  },
});

export const parseAsIntegerRange = createParser<NumberRangeValue>({
  parse: (value) => {
    if (!value) return null;
    const [startStr, endStr] = value.split("~");
    if (!startStr || !endStr) return null;
    const start = parseInt(startStr);
    const end = parseInt(endStr);
    return { start: isNaN(start) ? null : start, end: isNaN(end) ? null : end };
  },
  serialize: (val) => {
    if (!val) return "";
    return `${val.start ?? "-Infinity"}~${val.end ?? "+Infinity"}`;
  },
});

export const parseAsIntegerWithAll = createParser<number | "all">({
  parse: (value) => {
    if (value === "all") return "all";
    const int = parseInt(value, 10);
    if (isNaN(int)) return null;
    return int;
  },
  serialize: (val) => {
    if (val === "all") return "all";
    return String(val);
  },
});

export const parseAsIntegerArrayWithAll = createParser<number[] | "all">({
  parse: (value) => {
    if (value === "all") return "all";
    if (!value) return null;
    const parts = value.split(",").map((s) => parseInt(s, 10));
    if (parts.some((n) => isNaN(n))) return null;
    return parts;
  },
  serialize: (val) => {
    if (val === "all") return "all";
    return val.join(",");
  },
});

export const parseAsStringArrayWithAll = createParser<string[] | "all">({
  parse: (value) => {
    if (value === "all") return "all";
    if (!value) return null;
    return value.split(",").filter(Boolean);
  },
  serialize: (val) => {
    if (val === "all") return "all";
    return val.join(",");
  },
});

export const parseAsTime = createParser<Time>({
  parse: (value) => {
    if (!value) return null;
    const [hhRaw, mmRaw, ssRaw] = value.split(":");
    const hh = Number(hhRaw);
    const mm = Number(mmRaw);
    const ss = ssRaw == null ? 0 : Number(ssRaw);
    if (Number.isNaN(hh) || Number.isNaN(mm) || Number.isNaN(ss)) return null;
    return new Time(hh, mm, ss);
  },
  serialize: (val) => {
    if (!val) return "";
    const hh = String(val.hour).padStart(2, "0");
    const mm = String(val.minute).padStart(2, "0");
    return `${hh}:${mm}`;
  },
});

export const parseAsDate = createParser<CalendarDate>({
  parse: (value) => {
    try {
      if (!value) return null;
      return parseDate(value);
    } catch (error) {
      console.error("Could not parse date", error);
      return null;
    }
  },
  serialize: (val) => {
    return val?.toString() ?? "";
  },
});
